import React, { useEffect, useRef, useState, useCallback } from "react";

// presetUser: optionally open the modal straight into a conversation with a
// specific { user_id, name, email, user_type } — e.g. the "Message supplier"
// button on a supplier row — instead of landing on the empty user list.
function ChatModal({ isOpen, closeModal, presetUser }) {
  const [availableUsers, setAvailableUsers] = useState({});
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userToken, setUserToken] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Per-contact unread counts, keyed by user_id, shown as a badge next to
  // each name in the list (like the total unreadCount above, but scoped
  // per conversation instead of a single global figure).
  const [unreadByUser, setUnreadByUser] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const currentRoomRef = useRef(null);
  // handleWebSocketMessage reads this instead of the `selectedUser` state
  // directly — picking a different chat used to change handleWebSocketMessage's
  // identity, which cascaded into initializeWebSocket, which cascaded into
  // the connection effect, whose cleanup closes the socket before reopening
  // it. So every click on a different user tore down and rebuilt the whole
  // WebSocket connection ("Connected" → "Disconnected" on every click). A
  // ref lets the handler read the current selection without needing to be
  // recreated when it changes.
  const selectedUserRef = useRef(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachMenuRef = useRef(null);
  const galleryInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // Same idea, for `isOpen`: the connection needs to stay alive for the
  // whole admin session (so the header badge and per-contact counts can
  // update the moment a message arrives, not just while Live Chat happens
  // to be open) — so handleWebSocketMessage/initializeWebSocket must not
  // change identity every time the panel opens or closes either.
  const isOpenRef = useRef(isOpen);

  const API_BASE_URL = process.env.REACT_APP_URL;
  const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
  // Uploaded attachment files live under /uploads/... on the backend's own
  // origin, not under REACT_APP_URL (which already ends in /api).
  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_URL;

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes typing {
        0%, 60%, 100% { transform: translateY(0); }
        30% { transform: translateY(-5px); }
      }
      @keyframes cmIn {
        from { opacity: 0; transform: translateY(16px) scale(.985); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes msgIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes pulseRing {
        0% { box-shadow: 0 0 0 0 rgba(34,197,94,.4); }
        70% { box-shadow: 0 0 0 5px rgba(34,197,94,0); }
        100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
      }
      @keyframes cmAttachIn {
        from { opacity: 0; transform: translateY(6px) scale(.97); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes cmSpin {
        to { transform: rotate(360deg); }
      }
      .cm-attach-option { transition: background .12s ease; }
      .cm-attach-option:hover { background: var(--color-hover); }
      .cm-scroll::-webkit-scrollbar { width: 8px; }
      .cm-scroll::-webkit-scrollbar-thumb { background: var(--color-border); border-radius: 8px; border: 2px solid transparent; background-clip: content-box; }
      .cm-scroll::-webkit-scrollbar-track { background: transparent; }
      .cm-search-input::placeholder { color: rgba(255,255,255,.75); }
      .cm-user-row { transition: background .12s ease; }
      .cm-user-row:hover { background: var(--color-hover); }
      .cm-user-row.is-selected:hover { background: var(--color-surface); }
      .cm-icon-btn { transition: background .12s ease, color .12s ease; }
      .cm-icon-btn:hover { background: var(--color-hover); color: var(--color-text-primary); }
      .cm-msg-input { transition: border-color .12s ease, box-shadow .12s ease; }
      .cm-msg-input:focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-primary-weak); }
      .cm-send-btn { transition: filter .12s ease, transform .12s ease; }
      .cm-send-btn:not(:disabled):hover { filter: brightness(1.06); transform: translateY(-1px); }
      .cm-send-btn:not(:disabled):active { transform: translateY(0); }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("Token") || "";
    setUserToken(token);

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setCurrentUser({
          id: payload.user_id,
          username: payload.username,
          email: payload.email,
          user_type: payload.user_type,
        });
      } catch (err) {
        console.warn("Token decode failed", err);
      }
    }
  }, []);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  const getDefaultAdminContacts = useCallback(
    () => ({
      admins: [
        {
          user_id: 1,
          name: "Admin Support",
          email: "admin@packgenie.com",
          user_type: "admin",
        },
      ],
    }),
    [],
  );

  const handleWebSocketMessage = useCallback(
    (data) => {
      switch (data.action) {
        case "auth_success":
          setIsConnected(true);
          setTimeout(() => getOnlineUsers(), 500);
          break;

        case "auth_error":
          console.error("Auth error:", data.message);
          setIsConnected(false);
          break;

        case "room_joined":
          currentRoomRef.current = data.roomId;
          break;

        case "message_sent":
          // Only update message ID and timestamp, don't add duplicate
          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.message_id?.toString().startsWith("tmp-")) {
                return {
                  ...msg,
                  message_id: data.messageId,
                  created_at: data.timestamp,
                };
              }
              return msg;
            }),
          );
          break;

        case "message_received":
          // Only add message if it belongs to current conversation
          if (
            selectedUserRef.current &&
            (data.senderId === selectedUserRef.current.user_id ||
              data.receiverId === selectedUserRef.current.user_id)
          ) {
            const newMessage = {
              message_id: data.messageId,
              sender_id: data.senderId,
              receiver_id: data.receiverId,
              message: data.message,
              attachment_url: data.attachmentUrl || null,
              attachment_type: data.attachmentType || null,
              attachment_name: data.attachmentName || null,
              created_at: data.timestamp,
              is_read: data.isRead || false,
              sender: data.senderInfo,
            };

            setMessages((prev) => {
              const exists = prev.some((m) => m.message_id === data.messageId);
              if (exists) return prev;
              return [...prev, newMessage];
            });

            if (
              isOpenRef.current &&
              selectedUserRef.current?.user_id === data.senderId
            ) {
              markAsRead(data.messageId, data.senderId);
            }
          } else {
            setUnreadCount((n) => n + 1);
            setUnreadByUser((prev) => ({
              ...prev,
              [data.senderId]: (prev[data.senderId] || 0) + 1,
            }));
            // Header's badge listens for this instead of waiting on its own
            // poll — the connection now stays open for the whole session
            // (see initializeWebSocket), so this can fire the instant a
            // message actually arrives, not just eventually.
            window.dispatchEvent(new CustomEvent("chat-unread-changed"));
          }
          break;

        case "messages_fetched":
          // Only update messages if they're for the current conversation
          if (data.otherUserId === selectedUserRef.current?.user_id) {
            if (Array.isArray(data.messages)) {
              setMessages(data.messages);
            }
          }
          break;

        case "marked_as_read":
          break;

        case "message_read":
          setMessages((prev) =>
            prev.map((msg) =>
              msg.message_id === data.messageId
                ? { ...msg, is_read: true, read_at: data.readAt }
                : msg,
            ),
          );
          break;

        case "online_users":
          if (Array.isArray(data.users)) {
            const onlineUserIds = new Set(data.users.map((u) => u.user_id));
            setOnlineUsers(onlineUserIds);

            // Only merge in a synthetic "admins" bucket for roles that are
            // still on the hardcoded getDefaultAdminContacts() fallback
            // (buyer/supplier) — subadmin now gets the real list from
            // getAvailableUsers, which already includes admins correctly
            // grouped under "admin" (singular). Merging here too for
            // subadmin created a second, duplicate admin section.
            if (!["admin", "subadmin"].includes(currentUser?.user_type)) {
              const adminUsers = data.users.filter(
                (u) => u.user_type === "admin" || u.user_type === "subadmin",
              );

              if (adminUsers.length > 0) {
                setAvailableUsers((prev) => {
                  const existingAdmins = prev.admins || [];
                  const mergedAdmins = [...existingAdmins];

                  adminUsers.forEach((admin) => {
                    if (
                      !mergedAdmins.find((u) => u.user_id === admin.user_id)
                    ) {
                      mergedAdmins.push({
                        user_id: admin.user_id,
                        name:
                          admin.name ||
                          admin.subAdmin?.name ||
                          `Admin ${admin.user_id}`,
                        email:
                          admin.email || `admin${admin.user_id}@packgenie.com`,
                        user_type: admin.user_type,
                      });
                    }
                  });

                  return { ...prev, admins: mergedAdmins };
                });
              }
            }
          }
          break;

        case "user_online":
          if (data.userId) {
            setOnlineUsers((prev) => new Set([...prev, data.userId]));
          }
          break;

        case "user_offline":
          if (data.userId) {
            setOnlineUsers((prev) => {
              const newSet = new Set(prev);
              newSet.delete(data.userId);
              return newSet;
            });
          }
          break;

        case "user_typing":
          if (data.userId && data.userId !== currentUser?.id) {
            if (data.isTyping) {
              setTypingUsers((prev) => new Set([...prev, data.userId]));
            } else {
              setTypingUsers((prev) => {
                const newSet = new Set(prev);
                newSet.delete(data.userId);
                return newSet;
              });
            }
          }
          break;

        case "error":
          console.error("WS error:", data.message);
          break;

        default:
      }
    },
    [currentUser],
  );

  const initializeWebSocket = useCallback(() => {
    if (!WS_URL || !userToken) {
      console.error("WebSocket URL or token not available");
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      wsRef.current = new WebSocket(WS_URL);

      wsRef.current.onopen = () => {
        authenticateWebSocket();
      };

      wsRef.current.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          handleWebSocketMessage(data);
        } catch (err) {
          console.error("WS message parse error", err);
        }
      };

      wsRef.current.onerror = (err) => {
        console.error("WebSocket error", err);
        setIsConnected(false);
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;

        // Always try to reconnect — this connection is meant to stay alive
        // for the whole admin session (so the header badge and per-contact
        // counts can update live), not just while the panel happens to be
        // open.
        setTimeout(() => {
          initializeWebSocket();
        }, 3000);
      };
    } catch (err) {
      console.error("WebSocket init error", err);
    }
  }, [WS_URL, userToken, handleWebSocketMessage]);

  const authenticateWebSocket = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && userToken) {
      wsRef.current.send(
        JSON.stringify({
          action: "authenticate",
          token: userToken,
        }),
      );
    }
  };

  const joinRoom = (receiverId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "join_room",
          receiverId: receiverId,
        }),
      );
    }
  };

  const getMessages = (otherUserId, page = 1, limit = 50) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "get_messages",
          otherUserId: otherUserId,
          page: page,
          limit: limit,
        }),
      );
    }
  };

  const markAsRead = (messageId, senderId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "mark_as_read",
          messageId: messageId,
          senderId: senderId,
        }),
      );
    }
  };

  const getOnlineUsers = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "get_online_users",
        }),
      );
    }
  };

  const sendTypingIndicator = (receiverId, isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && receiverId) {
      wsRef.current.send(
        JSON.stringify({
          action: "typing",
          receiverId: receiverId,
          isTyping: isTyping,
        }),
      );
    }
  };

  const fetchAvailableUsers = useCallback(async () => {
    // Subadmins (sales executives and graphic designers — both carry
    // user_type "subadmin") are staff too and now get a correctly-scoped
    // real contact list from /chat/available-users (see chatController.js),
    // same as admin. Only buyers/suppliers fall back to the single
    // hardcoded "Admin Support" contact.
    if (!["admin", "subadmin"].includes(currentUser?.user_type)) {
      setAvailableUsers(getDefaultAdminContacts());
      return;
    }

    if (!userToken || !API_BASE_URL) return;

    try {
      const res = await fetch(`${API_BASE_URL}/chat/available-users`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });

      if (!res.ok) {
        console.warn("Available users fetch failed:", res.status);
        return;
      }

      const data = await res.json();

      let usersData = {};
      if (Array.isArray(data)) {
        usersData = { all: data };
      } else if (Array.isArray(data.users)) {
        usersData = { all: data.users };
      } else if (typeof data.users === "object" && data.users !== null) {
        usersData = data.users;
      } else if (data.data && Array.isArray(data.data)) {
        usersData = { all: data.data };
      } else {
        usersData = data;
      }

      setAvailableUsers(usersData);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, [userToken, API_BASE_URL, currentUser, getDefaultAdminContacts]);

  // Per-conversation unread counts for the contact list badges.
  // /chat/conversations already computes unread_count per other_user_id
  // (see chatController.js's getConversations) — no new backend work
  // needed, just reading data that already existed.
  const fetchConversationUnreads = useCallback(async () => {
    if (!userToken || !API_BASE_URL) return;
    try {
      const res = await fetch(`${API_BASE_URL}/chat/conversations`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data?.conversations) ? data.conversations : [];
      const map = {};
      list.forEach((c) => {
        const count = Number(c.unread_count) || 0;
        if (count > 0) map[c.other_user_id] = count;
      });
      setUnreadByUser(map);

      // A conversation partner can have an unread message counted here
      // (and by the header's badge, which is equally unfiltered) while not
      // appearing anywhere in availableUsers — getAvailableUsers filters to
      // active accounts that currently pass the chat permission matrix, but
      // an existing message doesn't stop counting as unread just because
      // the sender's role/status changed since they sent it. Left alone,
      // that badge had nowhere in the sidebar to render next to: the header
      // showed an unread count the panel itself couldn't account for. Any
      // such conversation partner gets folded into a "Recent" bucket so
      // every badge has a row to sit next to and can still be opened.
      setAvailableUsers((prev) => {
        const known = new Set();
        Object.values(prev || {}).forEach((group) => {
          if (Array.isArray(group)) group.forEach((u) => known.add(u.user_id));
        });
        const missing = list
          .filter((c) => Number(c.unread_count) > 0 && !known.has(c.other_user_id))
          .map((c) => ({
            user_id: c.other_user_id,
            name: c.other_user_name || `User ${c.other_user_id}`,
            email: "",
            user_type: c.other_user_type,
            profile_image: c.other_user_image,
          }));
        if (missing.length === 0) return prev;
        return { ...prev, Recent: [...(prev?.Recent || []), ...missing] };
      });
    } catch (err) {
      console.error("Error fetching conversation unread counts:", err);
    }
  }, [userToken, API_BASE_URL]);

  const handleUserSelect = useCallback((user) => {
    if (!user) return;

    setSelectedUser(user);
    setLoading(true);
    setMessages([]); // Clear messages when switching users

    joinRoom(user.user_id);

    // Small delay to ensure room is joined
    setTimeout(() => {
      getMessages(user.user_id);
      setLoading(false);
    }, 200);

    setTypingUsers(new Set());
    setUnreadCount(0);
    setUnreadByUser((prev) => {
      if (!prev[user.user_id]) return prev;
      const next = { ...prev };
      delete next[user.user_id];
      return next;
    });
    window.dispatchEvent(new CustomEvent("chat-unread-changed"));
  }, []);

  const sendMessage = useCallback(() => {
    if (!inputMessage.trim() || !selectedUser) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const tempId = `tmp-${Date.now()}`;

      const optimisticMessage = {
        message_id: tempId,
        sender_id: currentUser?.id,
        receiver_id: selectedUser.user_id,
        message: inputMessage.trim(),
        created_at: new Date().toISOString(),
        is_read: false,
      };

      // Add message optimistically
      setMessages((prev) => [...prev, optimisticMessage]);

      // Send via WebSocket
      wsRef.current.send(
        JSON.stringify({
          action: "send_message",
          receiverId: selectedUser.user_id,
          message: inputMessage.trim(),
          messageType: "text",
        }),
      );

      if (isTyping) {
        sendTypingIndicator(selectedUser.user_id, false);
        setIsTyping(false);
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      setInputMessage("");
    } else {
      console.warn("WebSocket not connected");
    }
  }, [inputMessage, selectedUser, currentUser, isTyping]);

  // Attachments go over REST (multipart), not the WebSocket JSON protocol —
  // the backend saves the file, creates the message row, and pushes it to
  // the receiver over their existing WS connection itself (see
  // chatController.js's uploadAttachment); we just append the message it
  // hands back here for the sender's own side.
  const uploadAttachment = useCallback(
    async (file) => {
      if (!file || !selectedUser || !userToken || !API_BASE_URL) return;
      setShowAttachMenu(false);
      setUploadingAttachment(true);
      try {
        const formData = new FormData();
        formData.append("attachment", file);
        formData.append("receiverId", selectedUser.user_id);

        const res = await fetch(`${API_BASE_URL}/chat/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${userToken}` },
          body: formData,
        });
        const data = await res.json();

        if (!res.ok || !data.success) {
          alert(data?.message || "Failed to send attachment");
          return;
        }

        setMessages((prev) => [...prev, data.data]);
      } catch (err) {
        console.error("Attachment upload failed:", err);
        alert("Failed to send attachment");
      } finally {
        setUploadingAttachment(false);
      }
    },
    [selectedUser, userToken, API_BASE_URL],
  );

  const handleAttachmentFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (file) uploadAttachment(file);
  };

  useEffect(() => {
    if (!showAttachMenu) return;
    const onClickOutside = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showAttachMenu]);

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);

    if (!isTyping && e.target.value.trim() && selectedUser) {
      setIsTyping(true);
      sendTypingIndicator(selectedUser.user_id, true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping && selectedUser) {
        setIsTyping(false);
        sendTypingIndicator(selectedUser.user_id, false);
      }
    }, 2000);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Deliberately not gated on `isOpen` — this connection is meant to
    // stay alive for the whole admin session so the header badge and
    // per-contact counts can update the moment a message actually
    // arrives, instead of only while the panel happens to be open.
    if (currentUser) {
      fetchAvailableUsers();
      fetchConversationUnreads();
      initializeWebSocket();
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [currentUser, fetchAvailableUsers, fetchConversationUnreads, initializeWebSocket]);

  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      getOnlineUsers();
      fetchConversationUnreads();
    }, 10000);

    return () => clearInterval(interval);
  }, [isConnected, fetchConversationUnreads]);

  // Jump straight into the conversation with presetUser once the socket is
  // authenticated, instead of leaving the user to find them in the list —
  // this is what lets a "Message supplier" button on another page open this
  // modal pre-targeted at that supplier.
  useEffect(() => {
    if (isOpen && presetUser && isConnected) {
      setAvailableUsers((prev) => {
        const category = presetUser.user_type === "admin" ? "admins" : "contacts";
        const list = prev[category] || [];
        if (list.some((u) => u.user_id === presetUser.user_id)) return prev;
        return { ...prev, [category]: [...list, presetUser] };
      });
      handleUserSelect(presetUser);
    }
  }, [isOpen, presetUser, isConnected]);

  if (!isOpen) return null;

  const renderUserList = () => {
    if (!availableUsers || Object.keys(availableUsers).length === 0) {
      return (
        <div style={styles.noUsers}>
          <div style={styles.noUsersIcon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div style={styles.noUsersText}>
            {currentUser?.user_type === "admin"
              ? "No users available"
              : "No admin contacts available"}
          </div>
          {currentUser?.user_type !== "admin" && (
            <div style={styles.noUsersSubtext}>
              Admin users will appear here when they come online
            </div>
          )}
        </div>
      );
    }

    const query = userSearchQuery.trim().toLowerCase();
    const filteredUsers = query
      ? Object.fromEntries(
          Object.entries(availableUsers).map(([category, users]) => [
            category,
            Array.isArray(users)
              ? users.filter(
                  (u) =>
                    u.name?.toLowerCase().includes(query) ||
                    u.email?.toLowerCase().includes(query),
                )
              : users,
          ]),
        )
      : availableUsers;

    const categories = Object.keys(filteredUsers).filter(
      (category) =>
        Array.isArray(filteredUsers[category]) &&
        filteredUsers[category].length > 0,
    );

    if (query && categories.length === 0) {
      return (
        <div style={styles.noUsers}>
          <div style={styles.noUsersText}>No matches for "{userSearchQuery}"</div>
        </div>
      );
    }

    return (
      <div style={styles.userList}>
        {categories.map((category) => (
          <div key={category} style={styles.category}>
            <div style={styles.categoryHeader}>
              {category.replace(/_/g, " ").toUpperCase()}
              {currentUser?.user_type !== "admin" && category === "admins" && (
                <span style={styles.supportBadge}>Support</span>
              )}
            </div>
            {Array.isArray(filteredUsers[category]) &&
              filteredUsers[category].map((user) => (
                <div
                  key={user.user_id}
                  onClick={() => handleUserSelect(user)}
                  className={`cm-user-row${
                    selectedUser?.user_id === user.user_id ? " is-selected" : ""
                  }`}
                  style={{
                    ...styles.userItem,
                    ...(selectedUser?.user_id === user.user_id
                      ? styles.selectedUser
                      : {}),
                  }}
                >
                  <div
                    style={{
                      ...styles.userAvatar,
                      background: avatarGradient(user.user_id),
                    }}
                  >
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                    {onlineUsers.has(user.user_id) && (
                      <div style={styles.onlineIndicator} />
                    )}
                  </div>
                  <div style={styles.userInfo}>
                    <div style={styles.userName}>
                      {user.name}
                      {onlineUsers.has(user.user_id) ? (
                        <span style={styles.onlineText}> • Online</span>
                      ) : (
                        <span style={styles.offlineText}> • Offline</span>
                      )}
                    </div>
                    <div style={styles.userEmail}>{user.email}</div>
                    {currentUser?.user_type !== "admin" && (
                      <div style={styles.userRole}>
                        {user.user_type === "admin" ? "Admin" : "Support"}
                      </div>
                    )}
                  </div>
                  {unreadByUser[user.user_id] > 0 && (
                    <span style={styles.userUnreadBadge}>
                      {unreadByUser[user.user_id] > 99
                        ? "99+"
                        : unreadByUser[user.user_id]}
                    </span>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>
    );
  };

  const renderMessages = () => {
    if (loading) {
      return (
        <div style={styles.loading}>
          <div>Loading messages...</div>
        </div>
      );
    }

    if (!selectedUser) {
      return (
        <div style={styles.welcome}>
          <div style={styles.welcomeIcon}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
          </div>
          <div style={styles.welcomeTitle}>
            {currentUser?.user_type === "admin"
              ? "Welcome to Live Chat"
              : "Contact Admin Support"}
          </div>
          <div style={styles.welcomeText}>
            {currentUser?.user_type === "admin"
              ? "Select a user from the list to start messaging"
              : "Select an admin from the list to get support"}
          </div>
        </div>
      );
    }

    const isTypingFromSelectedUser = typingUsers.has(selectedUser.user_id);

    return (
      <div style={styles.messagesContainer}>
        {messages.map((msg, index) => {
          const isOwnMessage = msg.sender_id === currentUser?.id;
          const prev = messages[index - 1];
          const showDivider =
            !prev ||
            !sameDay(new Date(prev.created_at), new Date(msg.created_at));
          // Consecutive messages from the same sender, same day, sit
          // closer together — the usual chat-app "grouping" cue that
          // reads as one person talking, rather than a wall of identical
          // gaps between every single bubble.
          const isGrouped =
            !showDivider && prev && prev.sender_id === msg.sender_id;
          return (
            <React.Fragment key={msg.message_id ?? index}>
              {showDivider && (
                <div style={styles.dateDivider}>
                  <span style={styles.dateDividerLabel}>
                    {formatDateDivider(msg.created_at)}
                  </span>
                </div>
              )}
              <div
                style={{
                  ...styles.messageBubble,
                  ...(isOwnMessage ? styles.ownMessage : styles.otherMessage),
                  marginTop: isGrouped ? "-6px" : 0,
                }}
              >
                {msg.attachment_url &&
                  (msg.attachment_type === "image" ? (
                    <a
                      href={`${IMAGE_BASE_URL}${msg.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={`${IMAGE_BASE_URL}${msg.attachment_url}`}
                        alt={msg.attachment_name || "attachment"}
                        style={styles.attachmentImage}
                      />
                    </a>
                  ) : (
                    <a
                      href={`${IMAGE_BASE_URL}${msg.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...styles.attachmentDoc,
                        ...(isOwnMessage
                          ? styles.attachmentDocOwn
                          : styles.attachmentDocOther),
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span style={styles.attachmentDocName}>
                        {msg.attachment_name || "Document"}
                      </span>
                    </a>
                  ))}
                {msg.message && (
                  <div style={styles.messageText}>{msg.message}</div>
                )}
                <div style={styles.messageMeta}>
                  <span style={styles.messageTime}>
                    {new Date(msg.created_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  {isOwnMessage && (
                    <span
                      style={{
                        ...styles.readStatus,
                        color: msg.is_read
                          ? "#53BDEB" // WhatsApp-style blue once read
                          : "inherit",
                      }}
                    >
                      {msg.is_read ? "✓✓" : "✓"}
                    </span>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}

        {isTypingFromSelectedUser && (
          <div style={styles.typingIndicator}>
            <div style={styles.typingDots}>
              <span
                style={{ ...styles.typingDotsSpan, animationDelay: "0s" }}
              ></span>
              <span
                style={{ ...styles.typingDotsSpan, animationDelay: "0.2s" }}
              ></span>
              <span
                style={{ ...styles.typingDotsSpan, animationDelay: "0.4s" }}
              ></span>
            </div>
            <div style={styles.typingText}>
              {selectedUser.name} is typing...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    );
  };

  const CloseBtn = () => (
    <button
      type="button"
      aria-label="Close"
      onClick={closeModal}
      className="cm-icon-btn"
      style={styles.convoBtn}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinecap="round"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.chatModal}>
        <div style={styles.chatMain}>
          <div style={styles.sidebar}>
            {/* brand header (gradient) */}
            <div style={styles.sidebarBrand}>
              <div style={styles.brandBlob} />
              <div style={styles.brandRow}>
                <div style={styles.brandIcon}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textAlign: "left",
                    lineHeight: 1.15,
                  }}
                >
                  <div style={styles.brandTitle}>
                    {currentUser?.user_type === "admin"
                      ? "Live Chat"
                      : "Support Chat"}
                  </div>
                  <div style={styles.brandStatus}>
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: isConnected ? "#4ADE80" : "#f87171",
                      }}
                    />
                    {isConnected ? "Connected" : "Disconnected"}
                    {unreadCount > 0 && (
                      <span style={styles.unreadBadge}>{unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={styles.sidebarSearch}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,.85)"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  className="cm-search-input"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder={
                    currentUser?.user_type === "admin"
                      ? "Search users…"
                      : "Search admins…"
                  }
                  style={styles.sidebarSearchInput}
                />
              </div>
            </div>
            <div className="cm-scroll" style={styles.userListContainer}>
              {renderUserList()}
            </div>
          </div>

          <div style={styles.chatArea}>
            <div style={styles.selectedUserHeader}>
              {selectedUser ? (
                <>
                  <div
                    style={{
                      ...styles.selectedUserAvatar,
                      background: avatarGradient(selectedUser.user_id),
                    }}
                  >
                    {selectedUser.name?.charAt(0)?.toUpperCase() || "U"}
                    {onlineUsers.has(selectedUser.user_id) && (
                      <div style={styles.onlineIndicator} />
                    )}
                  </div>
                  <div style={styles.selectedUserInfo}>
                    <div style={styles.selectedUserName}>
                      {selectedUser.name}
                      {onlineUsers.has(selectedUser.user_id) ? (
                        <span style={styles.onlineText}> • Online</span>
                      ) : (
                        <span style={styles.offlineText}> • Offline</span>
                      )}
                    </div>
                    <div style={styles.selectedUserEmail}>
                      {selectedUser.email}
                    </div>
                    {currentUser?.user_type !== "admin" && (
                      <div style={styles.selectedUserRole}>
                        {selectedUser.user_type === "admin"
                          ? "Admin"
                          : "Support Team"}
                      </div>
                    )}
                  </div>
                  <div style={styles.convoActions}>
                    <button
                      type="button"
                      aria-label="Call"
                      title={
                        selectedUser.phone
                          ? `Call ${selectedUser.phone}`
                          : "No phone number on file"
                      }
                      onClick={() =>
                        selectedUser.phone &&
                        window.open(`tel:${selectedUser.phone}`)
                      }
                      disabled={!selectedUser.phone}
                      className="cm-icon-btn"
                      style={{
                        ...styles.convoBtn,
                        ...(!selectedUser.phone
                          ? { opacity: 0.4, cursor: "not-allowed" }
                          : {}),
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </button>
                    <CloseBtn />
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Messages
                  </div>
                  <div style={styles.convoActions}>
                    <CloseBtn />
                  </div>
                </>
              )}
            </div>

            <div className="cm-scroll" style={styles.messagesArea}>
              {renderMessages()}
            </div>

            {selectedUser && (
              <div style={styles.inputContainer}>
                <div style={{ position: "relative" }} ref={attachMenuRef}>
                  <button
                    type="button"
                    aria-label="Attach"
                    onClick={() => setShowAttachMenu((v) => !v)}
                    disabled={uploadingAttachment}
                    className="cm-icon-btn"
                    style={styles.attachBtn}
                  >
                    {uploadingAttachment ? (
                      <span style={styles.attachSpinner} />
                    ) : (
                      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05 12.25 20.24a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95L9.42 17.41a1.5 1.5 0 0 1-2.12-2.12l8.49-8.49" />
                      </svg>
                    )}
                  </button>
                  {showAttachMenu && (
                    <div style={styles.attachMenu}>
                      <button
                        type="button"
                        className="cm-attach-option"
                        style={styles.attachMenuItem}
                        onClick={() => galleryInputRef.current?.click()}
                      >
                        <span style={{ ...styles.attachMenuIcon, background: "#8B5CF6" }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="m21 15-5-5L5 21" />
                          </svg>
                        </span>
                        Photos
                      </button>
                      <button
                        type="button"
                        className="cm-attach-option"
                        style={styles.attachMenuItem}
                        onClick={() => documentInputRef.current?.click()}
                      >
                        <span style={{ ...styles.attachMenuIcon, background: "#6366F1" }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </span>
                        Document
                      </button>
                      <button
                        type="button"
                        className="cm-attach-option"
                        style={styles.attachMenuItem}
                        onClick={() => cameraInputRef.current?.click()}
                      >
                        <span style={{ ...styles.attachMenuIcon, background: "#F59E0B" }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                          </svg>
                        </span>
                        Camera
                      </button>
                    </div>
                  )}
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*,video/*"
                    style={{ display: "none" }}
                    onChange={handleAttachmentFileChange}
                  />
                  <input
                    ref={documentInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: "none" }}
                    onChange={handleAttachmentFileChange}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={handleAttachmentFileChange}
                  />
                </div>
                <input
                  type="text"
                  value={inputMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="cm-msg-input"
                  style={styles.messageInput}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || !isConnected}
                  className="cm-send-btn"
                  style={{
                    ...styles.sendButton,
                    ...(!inputMessage.trim() || !isConnected
                      ? styles.sendButtonDisabled
                      : {}),
                  }}
                >
                  Send
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const BRAND_GRADIENT =
  "linear-gradient(135deg, var(--color-primary), var(--color-primary-2))";

// Distinct gradient per user (deterministic), matching the reference's colourful avatars.
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#38BDF8,#2563EB)",
  "linear-gradient(135deg,#818CF8,#4F46E5)",
  "linear-gradient(135deg,#34D399,#059669)",
  "linear-gradient(135deg,#FBBF24,#D97706)",
  "linear-gradient(135deg,#22D3EE,#0891B2)",
  "linear-gradient(135deg,#A78BFA,#7C3AED)",
  "linear-gradient(135deg,#FB7185,#E11D48)",
];
const avatarGradient = (id) => {
  const s = String(id ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
};

// "Today" / "Yesterday" / "Mon, 15 Sep" — the usual chat-app date divider,
// and the same day-boundary check used to decide when to show one.
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDateDivider = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (sameDay(date, today)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
};

const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(12,20,36,.42)",
    backdropFilter: "blur(2px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "24px",
    fontFamily:
      "'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    letterSpacing: "-0.01em",
  },
  chatModal: {
    width: "880px",
    maxWidth: "100%",
    height: "664px",
    maxHeight: "88vh",
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "20px",
    boxShadow: "0 10px 30px rgba(16,24,40,.14), 0 30px 70px rgba(16,24,40,.18)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "cmIn .34s cubic-bezier(.2,.8,.2,1) both",
  },
  chatHeader: {
    position: "relative",
    background: BRAND_GRADIENT,
    color: "#fff",
    padding: "16px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    overflow: "hidden",
  },
  chatHeaderInfo: {
    flex: 1,
    position: "relative",
    zIndex: 1,
  },
  supportName: {
    fontWeight: 800,
    fontSize: "16px",
    letterSpacing: "-.02em",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  connectionStatus: {
    fontSize: "11.5px",
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
    opacity: 0.92,
  },
  statusDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    marginRight: "6px",
  },
  unreadBadge: {
    backgroundColor: "rgba(255,255,255,.22)",
    color: "#fff",
    borderRadius: "10px",
    padding: "2px 7px",
    fontSize: "10px",
    fontWeight: 700,
    marginLeft: "8px",
  },
  userUnreadBadge: {
    flex: "0 0 auto",
    minWidth: "20px",
    height: "20px",
    padding: "0 6px",
    borderRadius: "10px",
    background: "var(--color-primary)",
    color: "#fff",
    fontSize: "11px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  closeButton: {
    position: "relative",
    zIndex: 1,
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    width: "32px",
    height: "32px",
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,.16)",
    border: "1px solid rgba(255,255,255,.22)",
  },
  chatMain: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    minHeight: 0,
  },
  sidebar: {
    width: "312px",
    flex: "0 0 312px",
    borderRight: "1px solid var(--color-border)",
    display: "flex",
    flexDirection: "column",
    background: "var(--color-surface-2)",
    minHeight: 0,
  },
  sidebarBrand: {
    position: "relative",
    background: BRAND_GRADIENT,
    color: "#fff",
    padding: "18px 18px 16px",
    overflow: "hidden",
    flex: "0 0 auto",
  },
  brandRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "11px",
    zIndex: 1,
  },
  brandIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    background: "rgba(255,255,255,.16)",
    border: "1px solid rgba(255,255,255,.22)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 36px",
  },
  brandTitle: { fontSize: "16px", fontWeight: 800, letterSpacing: "-.02em" },
  brandStatus: {
    fontSize: "11px",
    opacity: 0.9,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "2px",
  },
  brandBlob: {
    position: "absolute",
    top: "-36px",
    right: "-26px",
    width: "130px",
    height: "130px",
    borderRadius: "50%",
    background: "rgba(255,255,255,.09)",
  },
  sidebarSearch: {
    position: "relative",
    zIndex: 1,
    marginTop: "14px",
    display: "flex",
    alignItems: "center",
    gap: "9px",
    height: "40px",
    padding: "0 13px",
    background: "rgba(255,255,255,.16)",
    border: "1px solid rgba(255,255,255,.2)",
    borderRadius: "11px",
  },
  sidebarSearchInput: {
    border: 0,
    outline: 0,
    background: "transparent",
    fontFamily: "inherit",
    fontSize: "13px",
    color: "#fff",
    width: "100%",
  },
  convoActions: { marginLeft: "auto", display: "flex", gap: "7px" },
  convoBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: "var(--color-text-secondary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  searchContainer: {
    padding: "14px 14px",
    borderBottom: "1px solid var(--color-border)",
    background: "var(--color-surface)",
  },
  searchInput: {
    width: "100%",
    height: "40px",
    padding: "0 14px",
    borderRadius: "11px",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface-2)",
    color: "var(--color-text-primary)",
    outline: "none",
    fontSize: "13px",
    fontFamily: "inherit",
  },
  userListContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },
  userList: {
    padding: "8px 8px 12px",
  },
  category: {
    marginBottom: "6px",
  },
  categoryHeader: {
    padding: "12px 10px 7px",
    fontSize: "10.5px",
    fontWeight: 800,
    letterSpacing: ".08em",
    color: "var(--color-text-tertiary)",
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
  },
  userItem: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    padding: "10px 11px",
    cursor: "pointer",
    borderRadius: "13px",
    marginBottom: "4px",
    transition: "background 0.15s ease",
  },
  selectedUser: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-primary-weak)",
    boxShadow: "0 1px 3px rgba(16,24,40,.05)",
  },
  userAvatar: {
    width: "42px",
    height: "42px",
    borderRadius: "13px",
    background: BRAND_GRADIENT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: 700,
    fontSize: "15px",
    position: "relative",
    flexShrink: 0,
  },
  onlineIndicator: {
    position: "absolute",
    bottom: "-1px",
    right: "-1px",
    width: "12px",
    height: "12px",
    backgroundColor: "#22C55E",
    border: "2.5px solid var(--color-surface-2)",
    borderRadius: "50%",
    animation: "pulseRing 2.2s infinite",
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
    lineHeight: 1.35,
  },
  userName: {
    fontSize: "13.5px",
    fontWeight: 700,
    color: "var(--color-text-primary)",
    marginBottom: "2px",
    display: "flex",
    alignItems: "center",
  },
  onlineText: {
    fontSize: "11px",
    color: "#16A34A",
    fontWeight: 600,
    marginLeft: "4px",
  },
  offlineText: {
    fontSize: "11px",
    color: "var(--color-text-tertiary)",
    fontWeight: "normal",
    marginLeft: "4px",
  },
  userEmail: {
    fontSize: "12px",
    color: "var(--color-text-secondary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userRole: {
    fontSize: "10px",
    color: "var(--color-primary)",
    fontWeight: 700,
    marginTop: "2px",
  },
  noUsers: {
    padding: "40px 20px",
    textAlign: "center",
    color: "var(--color-text-secondary)",
    fontSize: "14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  noUsersIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "56px",
    height: "56px",
    borderRadius: "16px",
    background: "var(--color-surface-2)",
    color: "var(--color-text-tertiary)",
    marginBottom: "14px",
  },
  noUsersText: {
    fontSize: "15px",
    fontWeight: 700,
    marginBottom: "8px",
    color: "var(--color-text-primary)",
  },
  noUsersSubtext: {
    fontSize: "12px",
    color: "var(--color-text-tertiary)",
    lineHeight: "1.4",
    maxWidth: "200px",
  },
  supportBadge: {
    fontSize: "9px",
    background: "var(--color-primary-weak)",
    color: "var(--color-primary)",
    padding: "2px 7px",
    borderRadius: "10px",
    fontWeight: 700,
    marginLeft: "8px",
  },
  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    background: "var(--color-surface)",
  },
  selectedUserHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid var(--color-border)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    background: "var(--color-surface)",
  },
  selectedUserAvatar: {
    width: "44px",
    height: "44px",
    borderRadius: "13px",
    background: BRAND_GRADIENT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: 700,
    fontSize: "16px",
    position: "relative",
    flexShrink: 0,
  },
  selectedUserInfo: {
    flex: 1,
    minWidth: 0,
    lineHeight: 1.3,
  },
  selectedUserName: {
    fontSize: "15px",
    fontWeight: 800,
    color: "var(--color-text-primary)",
    display: "flex",
    alignItems: "center",
  },
  selectedUserEmail: {
    display: "flex",
    fontSize: "12px",
    color: "var(--color-text-secondary)",
  },
  selectedUserRole: {
    fontSize: "11px",
    color: "var(--color-primary)",
    fontWeight: 700,
    marginTop: "2px",
  },
  messagesArea: {
    flex: 1,
    padding: "20px",
    overflowY: "auto",
    background: "var(--color-surface-2)",
    minHeight: 0,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "var(--color-text-secondary)",
  },
  welcome: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    color: "var(--color-text-secondary)",
  },
  welcomeIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: "var(--color-primary-weak)",
    color: "var(--color-primary)",
    marginBottom: "16px",
  },
  welcomeTitle: {
    fontSize: "18px",
    fontWeight: 700,
    marginBottom: "8px",
    color: "var(--color-text-primary)",
  },
  welcomeText: {
    fontSize: "14px",
  },
  messagesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  dateDivider: {
    display: "flex",
    justifyContent: "center",
    margin: "6px 0 2px",
  },
  dateDividerLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: "var(--color-text-tertiary)",
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "999px",
    padding: "4px 12px",
  },
  messageBubble: {
    maxWidth: "76%",
    padding: "10px 13px",
    fontSize: "13px",
    lineHeight: 1.5,
    borderRadius: "14px",
    position: "relative",
    animation: "msgIn .3s ease both",
  },
  ownMessage: {
    alignSelf: "flex-end",
    background: BRAND_GRADIENT,
    color: "#fff",
    borderRadius: "14px 14px 4px 14px",
  },
  otherMessage: {
    alignSelf: "flex-start",
    background: "var(--color-surface)",
    color: "var(--color-text-primary)",
    border: "1px solid var(--color-border)",
    borderRadius: "14px 14px 14px 4px",
    boxShadow: "0 1px 2px rgba(16,24,40,.05)",
  },
  messageText: {
    fontSize: "13px",
    lineHeight: "1.5",
    wordBreak: "break-word",
  },
  messageMeta: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "5px",
    marginTop: "4px",
    opacity: 0.75,
  },
  messageTime: {
    fontSize: "10px",
  },
  readStatus: {
    fontSize: "10px",
    fontWeight: 700,
  },
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "14px",
    alignSelf: "flex-start",
    maxWidth: "170px",
  },
  typingDots: {
    display: "flex",
    gap: "3px",
  },
  typingDotsSpan: {
    width: "6px",
    height: "6px",
    backgroundColor: "var(--color-text-tertiary)",
    borderRadius: "50%",
    animation: "typing 1.4s infinite ease-in-out",
  },
  typingText: {
    fontSize: "12px",
    color: "var(--color-text-secondary)",
    fontStyle: "italic",
  },
  inputContainer: {
    padding: "14px 20px",
    borderTop: "1px solid var(--color-border)",
    display: "flex",
    gap: "11px",
    alignItems: "center",
    background: "var(--color-surface)",
  },
  attachBtn: {
    width: "40px",
    height: "40px",
    flex: "0 0 40px",
    borderRadius: "50%",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface-2)",
    color: "var(--color-text-secondary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  attachSpinner: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    border: "2px solid var(--color-border)",
    borderTopColor: "var(--color-primary)",
    animation: "cmSpin .7s linear infinite",
  },
  attachMenu: {
    position: "absolute",
    bottom: "48px",
    left: 0,
    width: "180px",
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "14px",
    boxShadow: "0 10px 30px rgba(16,24,40,.16)",
    padding: "6px",
    zIndex: 5,
    animation: "cmAttachIn .16s ease both",
  },
  attachMenuItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "9px 10px",
    borderRadius: "9px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--color-text-primary)",
    fontFamily: "inherit",
    textAlign: "left",
  },
  attachMenuIcon: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 30px",
  },
  attachmentImage: {
    display: "block",
    maxWidth: "220px",
    maxHeight: "220px",
    borderRadius: "10px",
    objectFit: "cover",
    cursor: "pointer",
  },
  attachmentDoc: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "9px 11px",
    borderRadius: "10px",
    color: "inherit",
    textDecoration: "none",
    maxWidth: "220px",
  },
  attachmentDocOwn: {
    background: "rgba(255,255,255,.16)",
    border: "1px solid rgba(255,255,255,.22)",
  },
  attachmentDocOther: {
    background: "var(--color-surface-2)",
    border: "1px solid var(--color-border)",
  },
  attachmentDocName: {
    fontSize: "12.5px",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  messageInput: {
    flex: 1,
    height: "46px",
    padding: "0 18px",
    borderRadius: "24px",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface-2)",
    color: "var(--color-text-primary)",
    outline: "none",
    fontSize: "14px",
    fontFamily: "inherit",
    minWidth: 0,
  },
  sendButton: {
    height: "46px",
    padding: "0 24px",
    background: BRAND_GRADIENT,
    border: "none",
    color: "white",
    borderRadius: "24px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "14px",
    fontFamily: "inherit",
    minWidth: "84px",
    boxShadow: "0 4px 12px rgba(79,70,229,.32)",
  },
  sendButtonDisabled: {
    background: "var(--color-border)",
    color: "var(--color-text-tertiary)",
    boxShadow: "none",
    cursor: "not-allowed",
  },
};

export default ChatModal;
